/** 
 * Transform all values of an object. Like array map.
 * 
 * @example
 * {
 *   "key1": fn(obj[key1]), // Value returned from fn() becomes the new value.
 *   "key2": fn(obj[key2]),
 *   "key3": fn(obj[key3]),
 * }
 */
export function mapObject<TSource extends object, TResultValue>(
	obj: TSource,
	fn: (value: TSource[keyof TSource], key: keyof TSource, index: number) => TResultValue
): Record<keyof TSource, TResultValue> 
{
	const newObj = {} as { [key in keyof TSource]: TResultValue };
	const keys = Object.keys(obj) as (keyof TSource)[];
	const values = Object.values(obj) as TSource[keyof TSource][];

	for (let i = 0; i < keys.length; ++i) 
	{
		const key = keys[i];
		const value = values[i];
		newObj[key] = fn(value, key, i);
	}

	return newObj;
}

/** 
 * Remove keys from an object. Like array filter.
 * 
 * @example
 * {
 *   "key1": fn(obj[key1]), // If fn() returns false, the key is removed.
 *   "key2": fn(obj[key2]),
 *   "key3": fn(obj[key3]),
 * }
 */
export function filterObject<TSource extends object, T>(
	obj: TSource,
	fn: (value: TSource[keyof TSource], key: keyof TSource, index: number) => boolean
): Record<keyof TSource, TSource[keyof TSource]> 
{
	const newObj = {} as { [key in keyof TSource]: TSource[keyof TSource] };
	const keys = Object.keys(obj) as (keyof TSource)[];
	const values = Object.values(obj) as TSource[keyof TSource][];

	for (let i = 0; i < keys.length; ++i) 
	{
		const key = keys[i];
		const value = values[i];
		if (fn(value, key, i)) 
		{
			newObj[key] = obj[key];
		}
	}

	return newObj;
}